import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/backend/lib/firebase';
import type { PolicySettings } from '@/shared/types/policySettings';
import { uploadPolicyPDF, deletePolicyPDF } from './storage/policies';

const SETTINGS_REF = () => doc(db, 'settings', 'policies');

export async function getPolicySettings(): Promise<PolicySettings> {
  const snap = await getDoc(SETTINGS_REF());
  if (!snap.exists()) return {};
  return snap.data() as PolicySettings;
}

interface SetPolicyParams {
  file: File;
  updatedBy: string;
  onProgress?: (progress: number) => void;
}

async function setPolicy(
  key: 'cancellation' | 'landlord',
  { file, updatedBy, onProgress }: SetPolicyParams,
): Promise<void> {
  const previous = await getPolicySettings();
  const previousPath =
    key === 'cancellation' ? previous.cancellationFilePath : previous.landlordFilePath;

  const { url, path } = await uploadPolicyPDF({ file, key, onProgress });

  const fieldUpdate =
    key === 'cancellation'
      ? {
          cancellationFileUrl: url,
          cancellationFilePath: path,
          cancellationFileName: file.name,
          cancellationUpdatedAt: serverTimestamp(),
          cancellationUpdatedBy: updatedBy,
        }
      : {
          landlordFileUrl: url,
          landlordFilePath: path,
          landlordFileName: file.name,
          landlordUpdatedAt: serverTimestamp(),
          landlordUpdatedBy: updatedBy,
        };

  await setDoc(SETTINGS_REF(), fieldUpdate, { merge: true });

  if (previousPath && previousPath !== path) {
    await deletePolicyPDF(previousPath);
  }
}

export async function setCancellationPolicy(params: SetPolicyParams): Promise<void> {
  return setPolicy('cancellation', params);
}

export async function setLandlordPolicy(params: SetPolicyParams): Promise<void> {
  return setPolicy('landlord', params);
}
